import { z } from "zod";

/**
 * CDL v1 — DSL de desbloqueos when/effect y eventos scriptados (B11).
 *
 * (El diseño lo llama "when/then"; la clave JSON es `effect` porque un objeto
 * con propiedad `then` es un thenable accidental: si alguna vez lo cruza un
 * await, Promise.resolve lo trata como promesa y el bug es indescifrable.)
 *
 * Declarativo en el caso, ejecutado por el engine (F1-T03): el motor evalúa
 * las condiciones contra el estado DERIVADO del log y aplica efectos. Nada de
 * esto se guarda: replay del log ⇒ mismos desbloqueos (saves intactos).
 */

export type ConditionV1Data =
  | { viewed: string }
  | { pinned: string }
  | { searched: string }
  | { milestone: string }
  | { requested: string }
  | { clockAtLeast: number }
  | { allOf: ConditionV1Data[] }
  | { anyOf: ConditionV1Data[] };

export const ConditionV1: z.ZodType<ConditionV1Data> = z.lazy(() =>
  z.union([
    z.object({ viewed: z.string() }), // artifactId visto
    z.object({ pinned: z.string() }), // artifactId fijado
    z.object({ searched: z.string() }), // término normalizado buscado
    z.object({ milestone: z.string() }), // hito resuelto
    z.object({ requested: z.string() }), // formulario Solicitar enviado (F1-T08)
    z.object({ clockAtLeast: z.number().int().min(0) }), // ticks del reloj
    z.object({ allOf: z.array(ConditionV1).min(1) }),
    z.object({ anyOf: z.array(ConditionV1).min(1) }),
  ]),
);

export const UnlockRuleV1 = z.object({
  id: z.string().min(1),
  when: ConditionV1,
  effect: z.object({
    unlockArtifacts: z.array(z.string().regex(/^A\d+$/)).default([]),
    notify: z.object({ title: z.string(), body: z.string() }).optional(),
    /** El efecto tarda N ticks en llegar (latencia diegética de Solicitar) */
    afterTicks: z.number().int().min(0).default(0),
  }),
});

/** Evento scriptado: la ausencia como pista ("ese post ya no está disponible") */
export const ScriptedEventV1 = z.object({
  id: z.string().min(1),
  when: ConditionV1,
  effect: z.object({
    removeArtifact: z
      .string()
      .regex(/^A\d+$/)
      .optional(),
    notify: z.object({ title: z.string(), body: z.string() }).optional(),
    afterTicks: z.number().int().min(0).default(1),
  }),
});

export type UnlockRuleV1Data = z.infer<typeof UnlockRuleV1>;
export type ScriptedEventV1Data = z.infer<typeof ScriptedEventV1>;
